import { Fragment } from "preact"
import { capture_event } from "@/utils/analytics"
import { useEffect, useMemo, useState } from "preact/hooks"

import { Link } from "wouter-preact"
import { CheckCircle2, ChevronDown, ChevronRight, Clock, Code, Download, Loader2, Receipt, RefreshCw, Wand2 } from "lucide-react"
import {
  ColumnFiltersState,
  RowSelectionState,
  SortingState,
  VisibilityState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  ClearPreviewResponse,
  OpenDeductionRow,
  OpenDeductionsResponse,
  clearOpenDeductions,
  listOpenDeductions,
  previewOpenDeductionClear,
  rebillOpenDeduction,
} from "@/api/accounting.tsx"
import { api_fetch } from "@/api/client.tsx"
import {
  FailedSyncReason,
  OPEN_ROWS_FILTERS,
  OpenRowsFilter,
  can_rebill,
  coding_text,
  common_origin_month,
  default_rebill_account_id,
  distinct_customers,
  distinct_values,
  failed_sync_reasons,
  month_end_date,
  month_label,
  open_deductions_csv,
  origin_month,
  origin_months,
  partial_sync_summary,
  readiness_bucket,
  row_status,
  selection_has_partial,
  selection_syncable,
  sum_row_amounts,
  sync_button_label,
} from "@/accounting/open_deductions_display.ts"
import { ColumnChoice, ColumnChoiceFilter, ColumnHeader } from "@/deduction_table/column_header"
import { ColumnVisibilityMenu } from "@/deduction_table/column_visibility_menu"
import { DEDUCTION_TABLE_SEARCH_PLACEHOLDER, SearchInput } from "@/deduction_table/debounced_input"
import { downloadCsv } from "@/deduction_table/util"
import { fuzzyFilter } from "@/utils/filters"
import { Badge } from "@/components/ui/badge"
import { DeductionKind } from "@/api/deduction"
import { CategoryCell } from "@/deduction_table/CategoryCell"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "@/components/ui/use-toast"
import { exactCurrencyFormatter, useAsyncEffect, usePageTitle } from "@/utils"
import { format_deduction_status_label } from "@/utils/deduction_status_labels"
import { format, parseISO } from "date-fns"
import { check_detail_path } from "./check_path"
import { LedgerAccounts } from "./accounts"
import { AutocompleteSelect, SelectItem as AccountItem } from "./autocomplete_select"
import { BulkCodingRow, bulk_code_to_account, bulk_full_code, start_bulk_coding_wiz_for_rows } from "./bulk_coding_actions"
import { CodePill } from "./code_pill"
import { FullCodingDialog } from "./FullCodingDialog.tsx"
import { OpenDeductionCodingPanel } from "./OpenDeductionCodingPanel.tsx"
import { Split } from "@/api/accounting.tsx"
import { use_coding_wiz_preferences } from "@/api/coding_wiz_preferences"
import { RefreshFlag, triggerRefreshFlag } from "@/refresh_flag.tsx"

const TABS: { key: "open" | "cleared"; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "cleared", label: "Cleared" },
]

/** Stages the queue lets a user move a selection to. Validated/Expensable are what unlock the sync. */
const STAGE_CHOICES: string[] = ["validated", "expensable", "lost", "review", "new"]

function today_iso(): string {
  return format(new Date(), "yyyy-MM-dd")
}

function SummaryCard({ title, amount, count, className }: { title: string; amount: number; count: number; className?: string }) {
  return (
    <Card className={className}>
      <CardHeader class="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle class="text-2xl">{exactCurrencyFormatter(amount)}</CardTitle>
      </CardHeader>
      <CardContent class="text-sm text-gray-500 pt-0">{count} deduction(s)</CardContent>
    </Card>
  )
}

function RowStatusBadge({ row }: { row: OpenDeductionRow }) {
  if (row.cleared_at) {
    return (
      <Badge variant="outline" className="text-green-700 border-green-300">
        Cleared {format(parseISO(row.cleared_at), "MMM d")}
      </Badge>
    )
  }
  if (row.clearing_pending) {
    return (
      <Badge variant="outline" className="text-amber-700 border-amber-300">
        Syncing…
      </Badge>
    )
  }
  if (row.sync_error) {
    return (
      <Badge variant="outline" className="text-red-700 border-red-300" title={row.sync_error}>
        Sync failed
      </Badge>
    )
  }
  if (row.ready) {
    return (
      <Badge variant="outline" className="text-green-700 border-green-300">
        Ready
      </Badge>
    )
  }
  if (!row.stage_ready) {
    return (
      <Badge variant="outline" className="text-sky-700 border-sky-300" title={row.blocked_reason}>
        Waiting on stage
      </Badge>
    )
  }
  if (row.partial) {
    return (
      <Badge
        variant="outline"
        className="text-amber-700 border-amber-300"
        title={`Syncing this row: ${partial_sync_summary(row.partial, exactCurrencyFormatter)}.`}>
        Partly coded
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-gray-600" title={row.blocked_reason}>
      Needs coding
    </Badge>
  )
}

/** The coded GL accounts chip list for a row (empty for uncoded rows). */

/**
 * The reference date the clear journal entry will carry for a row (`Ref <date>` in the line memo):
 * the deduction's execution date, and nothing else — a row without one shows a dash that links to
 * the deduction so the date can be set.
 */
function ReferenceDateCell({ row }: { row: Pick<OpenDeductionRow, "execution_date" | "deduction_id"> }) {
  if (row.execution_date) return <span>{format(parseISO(row.execution_date), "MMM d, yyyy")}</span>
  return (
    <Link
      href={`/deduction/${row.deduction_id}`}
      class="text-gray-400 hover:underline"
      title="No execution date on the deduction — set it on the deduction to give this line a reference date."
    >
      —
    </Link>
  )
}

/** The fields bulk coding needs from a queue row. */
function coding_row(row: OpenDeductionRow): BulkCodingRow {
  return {
    id: row.deduction_id,
    kind: row.kind as DeductionKind,
    invoice_amount: row.invoice_amount,
    discount_amount: row.discount_amount,
    invoice_number: row.invoice_number,
  }
}

interface RowCodingProps {
  row: OpenDeductionRow
  expanded: boolean
  /** Coding can change only while the row still sits in the holding account. */
  editable: boolean
  onToggle: () => void
}

/**
 * The row's GL coding at a glance — each coded account and amount — plus the way in: an uncoded row
 * gets a one-click "Code" picker, and the chevron opens the full coding form under the row.
 */
function RowCoding({ row, expanded, editable, onToggle }: RowCodingProps) {
  const coded = row.splits.filter((split) => split.account_number)

  async function handle_code(account: AccountItem) {
    if (await bulk_code_to_account([row.deduction_id], account.id)) {
      triggerRefreshFlag()
    }
  }

  return (
    <div class="flex items-start gap-1">
      {editable && (
        <button
          type="button"
          class="mt-0.5 text-gray-400 hover:text-gray-700"
          onClick={onToggle}
          aria-label={expanded ? "Hide coding" : "Edit coding"}
          title={expanded ? "Hide coding" : "Edit coding"}>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      )}
      {coded.length === 0 ? (
        editable ? (
          <AutocompleteSelect onSelect={handle_code} values={LedgerAccounts.get()} trigger={<CodePill />} searchPlaceholder="Search GL account..." />
        ) : (
          <span class="text-gray-400">—</span>
        )
      ) : (
        <button type="button" class="flex flex-wrap gap-1 text-left" onClick={editable ? onToggle : undefined} disabled={!editable}>
          {coded.map((split, i) => (
            <span key={i} class="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
              {split.account_number}
              {split.expense_account ? ` ${split.expense_account}` : ""} · {exactCurrencyFormatter(split.amount)}
            </span>
          ))}
        </button>
      )}
    </div>
  )
}

interface CodingActionsProps {
  selected_rows: OpenDeductionRow[]
  onDone: () => void
}

/** Bulk coding for the selection: one GL account, a full coding line, or a background CodingWiz run. */
function CodingActions({ selected_rows, onDone }: CodingActionsProps) {
  const { prefs: coding_wiz_prefs } = use_coding_wiz_preferences()
  const [full_coding_open, setFullCodingOpen] = useState(false)
  const rows = selected_rows.map(coding_row)

  async function handle_code_to_account(account: AccountItem) {
    if (await bulk_code_to_account(rows.map((row: BulkCodingRow) => row.id), account.id)) {
      triggerRefreshFlag()
      onDone()
    }
  }

  async function handle_full_code(values: Partial<Split>) {
    if (await bulk_full_code(rows, values)) {
      setFullCodingOpen(false)
      triggerRefreshFlag()
      onDone()
    }
  }

  function handle_coding_wiz() {
    if (start_bulk_coding_wiz_for_rows(rows, coding_wiz_prefs, true)) {
      onDone()
    }
  }

  return (
    <>
      <AutocompleteSelect
        onSelect={handle_code_to_account}
        values={LedgerAccounts.get()}
        trigger={<CodePill className="py-1.5 text-sm">Code</CodePill>}
        searchPlaceholder="Search GL account..."
      />
      <Button variant="outline" size="sm" class="gap-1" onClick={() => setFullCodingOpen(true)}>
        <Code className="h-4 w-4" />
        Fully code…
      </Button>
      <Button variant="outline" size="sm" class="gap-1" onClick={handle_coding_wiz}>
        <Wand2 className="h-4 w-4" />
        CodingWiz
      </Button>
      <FullCodingDialog
        open={full_coding_open}
        onOpenChange={setFullCodingOpen}
        onSubmit={handle_full_code}
        selectedTransactionIds={rows.map((row: BulkCodingRow) => row.id)}
      />
    </>
  )
}

interface ClearDialogProps {
  entry_ids: string[]
  selected_rows: OpenDeductionRow[]
  holding_label: string
  accounting_system: string
  onClose: () => void
  onCleared: () => void
}

/**
 * The review-before-post step: pick the posting date (with a one-click retroactive option when the
 * whole selection originates in one month), see the journal-entry impact per line, then post.
 */
function ClearDialog({ entry_ids, selected_rows, holding_label, accounting_system, onClose, onCleared }: ClearDialogProps) {
  const [posting_date, setPostingDate] = useState<string>(today_iso())
  const [preview, setPreview] = useState<ClearPreviewResponse | null>(null)
  const [preview_error, setPreviewError] = useState<string | null>(null)
  const [clearing, setClearing] = useState(false)

  const shared_month = common_origin_month(selected_rows)
  // A partly coded row syncs only its coded part; the batch has to ask for that.
  const allow_partial = selection_has_partial(selected_rows)

  useAsyncEffect(async () => {
    setPreview(null)
    setPreviewError(null)
    const res = await previewOpenDeductionClear({ entry_ids, posting_date, allow_partial })
    if (res.ok) {
      setPreview(res.value.data)
    } else {
      setPreviewError(res.error.message)
    }
  }, [entry_ids.join(","), posting_date])

  async function handleClear() {
    setClearing(true)
    const res = await clearOpenDeductions({ entry_ids, posting_date, allow_partial })
    setClearing(false)
    if (!res.ok) {
      toast({ title: "Clear to GL failed", description: res.error.message, variant: "destructive" })
      return
    }
    const data = res.value.data
    if (data.error) {
      toast({
        title: "Journal entry not posted",
        description: `${data.error} The rows are still open: fix the cause, then select them and sync again.`,
        variant: "destructive",
      })
      onCleared()
      return
    }
    capture_event("open_deductions_cleared", { accounting_system, cleared_count: data.cleared_count, even_wash: !data.journal_entry_id })
    toast({
      title: data.journal_entry_id ? `Cleared to ${accounting_system}` : "Cleared as an even wash",
      description: data.journal_entry_id
        ? `${data.cleared_count} deduction(s) (${exactCurrencyFormatter(data.cleared_total)}) reclassified out of ${holding_label}.${
            data.partial_count ? ` ${data.partial_count} cleared in part and stay open for the rest.` : ""
          }`
        : `${data.cleared_count} deduction(s) netted to zero, so nothing was posted.`,
    })
    onCleared()
  }

  return (
    <Dialog open onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Clear {selected_rows.length} deduction(s) to {accounting_system}
          </DialogTitle>
          <DialogDescription>
            Posts one journal entry: debit each deduction's coded GL account, credit {holding_label}. The original credit memo stays
            applied to its payment.
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-4">
          <div class="flex items-end gap-3">
            <div class="space-y-1">
              <Label>Posting date</Label>
              <Input type="date" value={posting_date} onChange={(e: Event) => setPostingDate((e.target as HTMLInputElement).value)} />
            </div>
            {shared_month && (
              <Button variant="outline" size="sm" onClick={() => setPostingDate(month_end_date(shared_month))}>
                Post in {month_label(shared_month)}
              </Button>
            )}
          </div>
          {shared_month === null && selected_rows.length > 1 && (
            <p class="text-xs text-amber-700">
              The selected deductions originate in different months. To post each month retroactively into its own period, run one
              batch per month.
            </p>
          )}

          {preview_error && <p class="text-sm text-red-600">{preview_error}</p>}
          {!preview && !preview_error && (
            <div class="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Building journal entry preview…
            </div>
          )}

          {preview && (
            <div class="rounded border max-h-[50vh] overflow-y-auto">
              <table class="w-full table-fixed text-sm">
                <thead>
                  <tr class="border-b bg-gray-50 text-left text-xs text-gray-500">
                    <th class="w-[34%] px-3 py-2">GL account</th>
                    <th class="w-[16%] px-3 py-2">Customer</th>
                    <th class="w-[34%] px-3 py-2">Description</th>
                    <th class="w-[16%] px-3 py-2 text-right">Debit / (credit)</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((line, i) => (
                    <tr key={i} class="border-b last:border-b-0 align-top">
                      <td class="px-3 py-2 break-words">
                        {line.gl_account_id}
                        {line.gl_account_name ? ` — ${line.gl_account_name}` : ""}
                      </td>
                      <td class="px-3 py-2 break-words">
                        {line.customer_name ? (
                          <span class="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">
                            {line.customer_name}
                          </span>
                        ) : (
                          <span class="text-gray-400">—</span>
                        )}
                      </td>
                      <td class="px-3 py-2 text-xs text-gray-600 break-words">{line.memo ?? ""}</td>
                      <td class="px-3 py-2 text-right font-mono whitespace-nowrap">
                        {line.amount >= 0 ? exactCurrencyFormatter(line.amount) : `(${exactCurrencyFormatter(Math.abs(line.amount))})`}
                      </td>
                    </tr>
                  ))}
                  <tr class="bg-gray-50 font-medium">
                    <td class="px-3 py-2" colSpan={3}>
                      {preview.holding_account}
                      {preview.holding_account_name ? ` — ${preview.holding_account_name}` : ""} (holding account)
                    </td>
                    <td class="px-3 py-2 text-right font-mono whitespace-nowrap">({exactCurrencyFormatter(preview.holding_total)})</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {preview && (preview.partial ?? []).length > 0 && (
            <div class="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <p class="font-medium">Partly coded — only the coded part is synced, the rest stays in {holding_label}:</p>
              <ul class="mt-1 list-disc pl-5">
                {(preview.partial ?? []).map((entry) => (
                  <li key={entry.entry_id}>
                    {entry.invoice_number || "(no invoice number)"}: {partial_sync_summary(entry, exactCurrencyFormatter)}.
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview && preview.blocked.length > 0 && (
            <p class="text-sm text-red-600">
              {preview.blocked.length} selected deduction(s) can't be synced: {preview.blocked.map((b) => `${b.invoice_number} (${b.reason})`).join(", ")}.
              Deselect them first.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={clearing}>
            Cancel
          </Button>
          <Button onClick={handleClear} disabled={clearing || !preview || preview.blocked.length > 0 || preview.lines.length === 0}>
            {clearing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Posting…
              </>
            ) : (
              `Post journal entry (${preview ? exactCurrencyFormatter(preview.holding_total) : "…"})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface StageDialogProps {
  selected_rows: OpenDeductionRow[]
  onClose: () => void
  onChanged: () => void
}

/**
 * Move a selection of parked deductions to a new stage in one go — the "yes, expense these" step
 * that makes `by_stage` rows syncable. Writes through the same bulk status endpoint the deduction
 * table uses.
 */
function StageDialog({ selected_rows, onClose, onChanged }: StageDialogProps) {
  const [stage, setStage] = useState<string>("validated")
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    const res = await api_fetch<void>("/statuses", {
      method: "POST",
      body: selected_rows.map((row) => ({ deduction_id: row.deduction_id, status: stage })),
    })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Stage update failed", description: res.error.message, variant: "destructive" })
      return
    }
    toast({
      title: "Stage updated",
      description: `${selected_rows.length} deduction(s) moved to ${format_deduction_status_label(stage)}.`,
    })
    onChanged()
  }

  return (
    <Dialog open onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Set stage on {selected_rows.length} deduction(s)</DialogTitle>
          <DialogDescription>
            Parked deductions sync to their GL accounts once their stage is closed out. Moving them to Validated or Expensable
            makes them ready; their coding is kept as-is.
          </DialogDescription>
        </DialogHeader>
        <div class="space-y-1">
          <Label>New stage</Label>
          <Select value={stage} onValueChange={(v: string) => setStage(v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_CHOICES.map((choice) => (
                <SelectItem key={choice} value={choice}>
                  {format_deduction_status_label(choice)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Update stage"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Keep a dialog open when the click lands in a popover it opened (the GL account picker). */
function keep_open_for_popover(e: Event) {
  const target = e.target as HTMLElement
  if (target?.closest?.("[data-radix-popper-content-wrapper]") || target?.closest?.("[cmdk-list]") || target?.closest?.("[cmdk-item]")) {
    e.preventDefault()
  }
}

interface RebillDialogProps {
  row: OpenDeductionRow
  accounting_system: string
  onClose: () => void
  onRebilled: () => void
}

/**
 * Bill a deduction back to the customer: creates the invoice the repayment will be applied to. The
 * GL account it credits is chosen here, defaulting to the deduction's own coding.
 */
export function RebillDialog({ row, accounting_system, onClose, onRebilled }: RebillDialogProps) {
  const accounts = LedgerAccounts.use()
  const default_account_id = default_rebill_account_id(row)
  const [account, setAccount] = useState<AccountItem | undefined>(undefined)
  const [invoice_date, setInvoiceDate] = useState<string>(today_iso())
  const [saving, setSaving] = useState(false)
  // The coded account is the default until the user picks another (the account list can load after
  // the dialog opens, so it is resolved on render rather than once).
  const chosen = account ?? accounts.find((a: AccountItem) => a.id === default_account_id)
  const uses_coding = !!chosen && chosen.id === default_account_id

  async function handleRebill() {
    if (!chosen) return
    setSaving(true)
    const res = await rebillOpenDeduction({ entry_id: row.entry_id, ledger_account_id: chosen.id, invoice_date })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Rebill failed", description: res.error.message, variant: "destructive" })
      return
    }
    const rebill = res.value.data.rebill
    toast({
      title: `Rebilled ${row.invoice_number}`,
      description: `Invoice ${rebill.invoice_number ?? rebill.invoice_remote_id} for ${exactCurrencyFormatter(rebill.amount)} is open in ${accounting_system}. Apply the repayment to it when it arrives.`,
    })
    onRebilled()
  }

  return (
    <Dialog open onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="max-w-lg" onInteractOutside={keep_open_for_popover}>
        <DialogHeader>
          <DialogTitle>Rebill {row.invoice_number}</DialogTitle>
          <DialogDescription>
            Creates an invoice in {accounting_system} to {row.customer_name} for {exactCurrencyFormatter(row.amount)}, so their repayment has
            an invoice to be applied to and stays tied to this deduction. The deduction itself stays in open deductions until it is synced.
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-4">
          <div class="space-y-1">
            <Label>GL account the invoice credits</Label>
            <AutocompleteSelect
              insideModal={true}
              values={accounts}
              initial={chosen}
              onSelect={(item: AccountItem) => setAccount(item)}
              searchPlaceholder="Search GL account..."
            />
            <p class="text-xs text-gray-500">
              {uses_coding
                ? "This is the account the deduction is coded to. Pick another if the repayment belongs somewhere else."
                : default_account_id
                  ? "Changed from the account the deduction is coded to."
                  : "This deduction isn't coded yet, so there is no default — pick the account it will be expensed to."}
            </p>
          </div>
          <div class="space-y-1 max-w-[12rem]">
            <Label>Invoice date</Label>
            <Input type="date" value={invoice_date} onChange={(e: Event) => setInvoiceDate((e.target as HTMLInputElement).value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleRebill} disabled={saving || !chosen || !invoice_date}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating invoice…
              </>
            ) : (
              `Create invoice (${exactCurrencyFormatter(row.amount)})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The row's rebill: the invoice it was billed back on, or the button that creates one. */
export function RebillCell({ row, onRebill }: { row: OpenDeductionRow; onRebill: () => void }) {
  if (row.rebill) {
    return (
      <Badge
        variant="outline"
        className="text-violet-700 border-violet-300 whitespace-nowrap"
        title={`Rebilled ${format(parseISO(row.rebill.invoice_date), "MMM d, yyyy")} for ${exactCurrencyFormatter(row.rebill.amount)}`}>
        Rebilled {row.rebill.invoice_number ?? ""}
      </Badge>
    )
  }
  if (!can_rebill(row)) return null
  return (
    <Button variant="ghost" size="sm" class="h-7 gap-1 px-2 text-gray-600" onClick={onRebill} title="Create an invoice for this deduction so its repayment can be applied">
      <Receipt className="h-3.5 w-3.5" />
      Rebill
    </Button>
  )
}

const columnHelper = createColumnHelper<OpenDeductionRow>()

/** Exact-match column filter (the choice selects store the cell value itself). */
function equals_filter(row: { getValue: (id: string) => unknown }, column_id: string, filter_value: unknown): boolean {
  return row.getValue(column_id) === filter_value
}

/** The quick filters' bucket (hidden column `readiness`) as a tanstack column filter value. */
function quick_filter_value(filters: ColumnFiltersState): OpenRowsFilter {
  const value = filters.find((f) => f.id === "readiness")?.value
  return typeof value === "string" ? (value as OpenRowsFilter) : "all"
}

/** Replace one column's filter, dropping it entirely for "no filter". */
function with_column_filter(filters: ColumnFiltersState, id: string, value: string | undefined): ColumnFiltersState {
  const rest = filters.filter((f) => f.id !== id)
  return value === undefined ? rest : [...rest, { id, value }]
}

/**
 * Open Deductions: everything cash application parked in the holding account — what is waiting on
 * a stage decision, what still needs coding, and what is ready to reclassify into its real GL
 * accounts — with a manual, review-first batch sync into the accounting system and a posting-date
 * choice for retroactive clears.
 *
 * The table is the same tanstack table the deduction list and check pages use: sort and filter
 * from any column header, search across every column (pasted lists match any value), hide
 * columns, export what is shown. The customer and month selects in the toolbar drive the same
 * column filters the headers do.
 */
export function OpenDeductionsPage() {
  usePageTitle("Open Deductions")
  const [data, setData] = useState<OpenDeductionsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<"open" | "cleared">("open")
  const [sorting, setSorting] = useState<SortingState>([{ id: "posted_date", desc: true }])
  const [column_filters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [global_filter, setGlobalFilter] = useState("")
  const [column_visibility, setColumnVisibility] = useState<VisibilityState>({ readiness: false })
  const [row_selection, setRowSelection] = useState<RowSelectionState>({})
  const [clear_dialog_open, setClearDialogOpen] = useState(false)
  const [stage_dialog_open, setStageDialogOpen] = useState(false)
  const [rebill_row, setRebillRow] = useState<OpenDeductionRow | null>(null)
  const [reload_nonce, setReloadNonce] = useState(0)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // Coding saved anywhere on the page (row form, CodingWiz, bulk actions) flips this flag.
  const refresh_flag = RefreshFlag.use((flag: boolean) => flag)

  useAsyncEffect(async () => {
    setLoading(true)
    const res = await listOpenDeductions()
    if (res.ok) {
      setData(res.value.data)
    } else {
      toast({ title: "Failed to load open deductions", description: res.error.message, variant: "destructive" })
    }
    setLoading(false)
  }, [reload_nonce, refresh_flag])

  const rows = data?.rows ?? []
  const open_rows = useMemo(() => rows.filter((row) => !row.cleared_at), [rows])
  const cleared_rows = useMemo(() => rows.filter((row) => !!row.cleared_at), [rows])
  const tab_rows = tab === "open" ? open_rows : cleared_rows
  const months = useMemo(() => origin_months(tab_rows), [tab_rows])
  const customers = useMemo(() => distinct_customers(tab_rows), [tab_rows])
  const stages = useMemo(() => distinct_values(tab_rows, (row: OpenDeductionRow) => row.stage), [tab_rows])
  const statuses = useMemo(() => distinct_values(tab_rows, row_status), [tab_rows])
  const failed_syncs = useMemo(() => failed_sync_reasons(open_rows), [open_rows])
  const by_stage = data?.scope === "by_stage"
  const accounting_system = data?.accounting_system ?? "accounting"
  // Rebilling creates an invoice in the accounting system; only NetSuite has that path today.
  const rebill_supported = data?.accounting_system === "NetSuite"

  // Switching tabs: the cleared rows have no selection or readiness, and a filter picked on one
  // tab rarely means anything on the other.
  useEffect(() => {
    setRowSelection({})
    setColumnFilters([])
    setColumnVisibility((visibility: VisibilityState) => ({ ...visibility, select: tab === "open" }))
  }, [tab])
  useEffect(() => {
    setColumnVisibility((visibility: VisibilityState) => ({ ...visibility, rebill: rebill_supported }))
  }, [rebill_supported])

  function toggle_expanded(entry_id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(entry_id)) {
        next.delete(entry_id)
      } else {
        next.add(entry_id)
      }
      return next
    })
  }

  const month_choices: ColumnChoice[] = useMemo(() => months.map((month: string) => ({ value: month, label: month_label(month) })), [months])
  const customer_choices: ColumnChoice[] = useMemo(() => customers.map((name: string) => ({ value: name, label: name })), [customers])
  const stage_choices: ColumnChoice[] = useMemo(
    () => stages.map((stage: string) => ({ value: stage, label: format_deduction_status_label(stage) })),
    [stages]
  )
  const status_choices: ColumnChoice[] = useMemo(() => statuses.map((status: string) => ({ value: status, label: status })), [statuses])

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && "indeterminate")}
            onCheckedChange={(value: boolean | "indeterminate") => table.toggleAllRowsSelected(!!value)}
            aria-label="Select all"
            className="h-4 w-4"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={() => row.toggleSelected()}
            aria-label={`Select ${row.original.invoice_number}`}
            className="h-4 w-4"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        enableGlobalFilter: false,
      }),
      columnHelper.accessor("invoice_number", {
        header: ({ column }) => <ColumnHeader column={column} title="Invoice" />,
        cell: ({ row }) => (
          <>
            <Link href={`/deduction/${row.original.deduction_id}`} class="text-blue-600 hover:underline">
              {row.original.invoice_number || "—"}
            </Link>
            {row.original.description && (
              <div class="max-w-xs truncate text-xs text-gray-500" title={row.original.description}>
                {row.original.description}
              </div>
            )}
          </>
        ),
        enableHiding: false,
      }),
      columnHelper.accessor("description", {
        header: ({ column }) => <ColumnHeader column={column} title="Description" />,
        cell: ({ getValue }) => <span class="text-xs text-gray-600">{getValue() ?? ""}</span>,
      }),
      columnHelper.accessor("customer_name", {
        header: ({ column }) => (
          <ColumnHeader column={column} title="Customer" filterContent={<ColumnChoiceFilter column={column} choices={customer_choices} all_label="All customers" className="min-w-56" />} />
        ),
        filterFn: equals_filter,
        enableHiding: false,
      }),
      columnHelper.accessor("check_number", {
        header: ({ column }) => <ColumnHeader column={column} title="Check" />,
        cell: ({ getValue }) => {
          const check_number = getValue()
          return check_number ? (
            <Link href={check_detail_path(check_number)} class="whitespace-nowrap text-blue-600 hover:underline">
              {check_number}
            </Link>
          ) : (
            "—"
          )
        },
      }),
      columnHelper.accessor("posted_date", {
        header: ({ column }) => <ColumnHeader column={column} title="Posted" />,
        cell: ({ getValue }) => <span class="whitespace-nowrap">{format(parseISO(getValue()), "MMM d, yyyy")}</span>,
      }),
      columnHelper.accessor("execution_date", {
        header: ({ column }) => <ColumnHeader column={column} title="Reference date" />,
        cell: ({ row }) => (
          <span class="whitespace-nowrap">
            <ReferenceDateCell row={row.original} />
          </span>
        ),
        meta: { columnPickerLabel: "Reference date" },
      }),
      columnHelper.accessor((row: OpenDeductionRow) => origin_month(row), {
        id: "origin",
        header: ({ column }) => (
          <ColumnHeader column={column} title="Origin" filterContent={<ColumnChoiceFilter column={column} choices={month_choices} all_label="All months" className="min-w-48" />} />
        ),
        cell: ({ getValue }) => <span class="whitespace-nowrap">{month_label(getValue())}</span>,
        filterFn: equals_filter,
      }),
      columnHelper.accessor((row: OpenDeductionRow) => row.category_name ?? "", {
        id: "category",
        header: ({ column }) => <ColumnHeader column={column} title="Category" />,
        cell: ({ row }) => (
          <CategoryCell
            deduction={{
              id: row.original.deduction_id,
              category: row.original.category_name ?? "",
              category_description: row.original.category_description ?? "",
              kind: row.original.kind as DeductionKind,
            }}
            onSaved={() => setReloadNonce((nonce: number) => nonce + 1)}
          />
        ),
      }),
      columnHelper.accessor("stage", {
        header: ({ column }) => (
          <ColumnHeader column={column} title="Stage" filterContent={<ColumnChoiceFilter column={column} choices={stage_choices} all_label="All stages" className="min-w-48" />} />
        ),
        cell: ({ row }) => (
          <Badge variant="outline" className={row.original.stage_ready ? "whitespace-nowrap text-blue-700 border-blue-200" : "whitespace-nowrap text-gray-600"}>
            {format_deduction_status_label(row.original.stage)}
          </Badge>
        ),
        filterFn: equals_filter,
      }),
      // Accessor is the coding text ("" when uncoded) so the column sorts (asc groups uncoded
      // rows first), searches, and filters by coded/uncoded — as on the check page.
      columnHelper.accessor((row: OpenDeductionRow) => coding_text(row), {
        id: "coding",
        header: ({ column }) => (
          <ColumnHeader
            column={column}
            title="Coding"
            choices={["coded", "uncoded"]}
            onSelectChoice={(choice: string) => column.setFilterValue(choice === "all" ? undefined : choice)}
          />
        ),
        filterFn: (row, column_id, filter_value) => (row.getValue<string>(column_id) ? "coded" : "uncoded") === filter_value,
        cell: ({ row }) => {
          const editable = !row.original.cleared_at && !row.original.clearing_pending
          return <RowCoding row={row.original} expanded={editable && expanded.has(row.original.entry_id)} editable={editable} onToggle={() => toggle_expanded(row.original.entry_id)} />
        },
      }),
      columnHelper.accessor("amount", {
        header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Amount" />,
        cell: ({ row }) => (
          <div class="text-right font-mono whitespace-nowrap">
            {exactCurrencyFormatter(row.original.amount)}
            {row.original.cleared_amount ? (
              <div class="font-sans text-xs text-gray-500">{exactCurrencyFormatter(row.original.cleared_amount)} already synced</div>
            ) : null}
          </div>
        ),
        enableHiding: false,
      }),
      columnHelper.accessor((row: OpenDeductionRow) => row_status(row), {
        id: "status",
        header: ({ column }) => (
          <ColumnHeader column={column} title="Status" filterContent={<ColumnChoiceFilter column={column} choices={status_choices} all_label="All statuses" className="min-w-48" />} />
        ),
        cell: ({ row }) => <RowStatusBadge row={row.original} />,
        filterFn: equals_filter,
      }),
      columnHelper.accessor((row: OpenDeductionRow) => row.rebill?.invoice_number ?? row.rebill?.invoice_remote_id ?? "", {
        id: "rebill",
        header: ({ column }) => <ColumnHeader column={column} title="Rebill" />,
        cell: ({ row }) => <RebillCell row={row.original} onRebill={() => setRebillRow(row.original)} />,
      }),
      // The quick filters' bucket: never shown, only filtered on.
      columnHelper.accessor((row: OpenDeductionRow) => readiness_bucket(row), {
        id: "readiness",
        filterFn: equals_filter,
        enableGlobalFilter: false,
        enableHiding: false,
      }),
    ],
    // The choice lists and the expanded set are read at render time by the cells and headers.
    [customer_choices, month_choices, stage_choices, status_choices, expanded]
  )

  const table = useReactTable({
    data: tab_rows,
    columns,
    state: {
      sorting,
      columnFilters: column_filters,
      globalFilter: global_filter,
      columnVisibility: column_visibility,
      rowSelection: row_selection,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getRowId: (row: OpenDeductionRow) => row.entry_id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuzzyFilter,
    getColumnCanGlobalFilter: (column) => column.columnDef.enableGlobalFilter ?? true,
    enableRowSelection: (row) => !row.original.clearing_pending,
    // No pagination here. Left on, tanstack queues a page-index reset whenever a row model
    // recomputes; that reset is a state write, which re-renders, which recomputes — a loop that
    // never yields (the page hung on first load with data). The other tables set it too.
    autoResetPageIndex: false,
  })

  const visible_rows = table.getRowModel().rows
  const selected_rows = useMemo(
    () => table.getSelectedRowModel().rows.map((row) => row.original).filter((row: OpenDeductionRow) => !row.cleared_at),
    [row_selection, tab_rows]
  )
  const filters_active = column_filters.length > 0 || global_filter.trim() !== ""
  const quick_filter = quick_filter_value(column_filters)
  const customer_column = table.getColumn("customer_name")
  const origin_column = table.getColumn("origin")

  function clear_filters() {
    setColumnFilters([])
    setGlobalFilter("")
  }

  function export_csv() {
    downloadCsv(open_deductions_csv(visible_rows.map((row) => row.original)), `open_deductions_${tab}`)
  }

  function handle_changed() {
    setClearDialogOpen(false)
    setStageDialogOpen(false)
    setRebillRow(null)
    setRowSelection({})
    setReloadNonce((n) => n + 1)
  }

  if (loading && !data) {
    return (
      <div class="flex items-center gap-2 p-8 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading open deductions…
      </div>
    )
  }

  if (data && !data.holding_account) {
    return (
      <div class="mx-auto max-w-2xl p-8">
        <Card>
          <CardHeader>
            <CardTitle>Open Deductions</CardTitle>
            <CardDescription>
              This screen tracks deductions that cash application parks in an open-deductions holding account, so cash posts daily and
              the decision to expense happens on your own cadence.
            </CardDescription>
          </CardHeader>
          <CardContent class="text-sm text-gray-600 space-y-2">
            <p>
              No holding account is configured yet. Pick the GL account to use as the holding account on the{" "}
              <Link href="/settings/accounting" class="underline">
                Settings › Accounting
              </Link>{" "}
              page, under Holding account and month end (Sage also needs the journal, e.g. GJ, that reclassification entries
              post to). You can also set it from the cash application preview of any check.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const summary = data?.summary
  const holding_label = data?.holding_account
    ? `${data.holding_account}${data.holding_account_name ? ` — ${data.holding_account_name}` : ""}`
    : ""
  const sage_journal_missing = data?.accounting_system === "Sage Intacct" && !data.gl_journal_id
  const can_sync = selection_syncable(selected_rows) && !sage_journal_missing
  const column_count = table.getVisibleLeafColumns().length

  return (
    <div class="space-y-4 p-6">
      <div class="flex items-start justify-between">
        <div>
          <h1 class="text-2xl font-semibold">Open Deductions</h1>
          <p class="text-sm text-gray-500">
            Holding account {holding_label} in {accounting_system}.{" "}
            {by_stage
              ? "Deductions that aren't yet Validated or Expensable (or aren't fully coded) post here when cash is applied; once a deduction's stage is closed out and it is coded, sync it to move the dollars to its GL account."
              : "Coded deductions can be synced to their GL accounts in batches."}{" "}
            Pick a posting date to post into the month a deduction originated in. A deduction coded for only part of its amount
            can sync that part; the rest stays here until it is coded.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setReloadNonce((n) => n + 1)} disabled={loading}>
          <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {summary && (
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <SummaryCard title="In holding account" amount={summary.open_total} count={summary.open_count} />
          <SummaryCard title="Ready to sync" amount={summary.ready_total} count={summary.ready_count} />
          <SummaryCard title={by_stage ? "Waiting on stage or coding" : "Needs coding"} amount={summary.uncoded_total} count={summary.uncoded_count} />
          <SummaryCard title="Cleared (last 90 days)" amount={summary.cleared_total} count={summary.cleared_count} />
        </div>
      )}

      {sage_journal_missing && (
        <p class="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          No Sage journal is configured for reclassification entries yet — syncing will be blocked until one is picked on the{" "}
          <Link href="/settings/connect/sageintacct" class="underline">
            Sage Intacct settings page
          </Link>
          .
        </p>
      )}

      {failed_syncs.length > 0 && (
        <div class="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          <p class="font-medium">{accounting_system} did not accept the last sync, so these deductions are still in the holding account.</p>
          <ul class="mt-1 list-disc pl-5">
            {failed_syncs.map(({ reason, count }: FailedSyncReason) => (
              <li key={reason}>
                {count} deduction(s): {reason}
              </li>
            ))}
          </ul>
          <p class="mt-1">Fix the cause, then select the rows marked "Sync failed" and sync them again.</p>
        </div>
      )}

      <div class="flex flex-wrap items-center gap-2">
        {TABS.map(({ key, label }) => (
          <Button key={key} variant={tab === key ? "default" : "outline"} size="sm" onClick={() => setTab(key)}>
            {label}
          </Button>
        ))}
        {tab === "open" && (
          <>
            <span class="mx-1 h-5 border-l" />
            {OPEN_ROWS_FILTERS.filter((f) => by_stage || f.key !== "needs_stage").map(({ key, label }) => (
              <Button
                key={key}
                variant={quick_filter === key ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setColumnFilters((filters: ColumnFiltersState) => with_column_filter(filters, "readiness", key === "all" ? undefined : key))}>
                {label}
              </Button>
            ))}
          </>
        )}
        <div class="ml-auto flex items-center gap-2">
          {customer_column && customers.length > 1 && (
            <div class="w-56">
              <ColumnChoiceFilter column={customer_column} choices={customer_choices} all_label="All customers" />
            </div>
          )}
          {origin_column && (
            <div class="w-44">
              <ColumnChoiceFilter column={origin_column} choices={month_choices} all_label="All months" />
            </div>
          )}
        </div>
      </div>

      <div class="flex items-center gap-2">
        <Button variant="outline" className="text-slate-500" onClick={clear_filters} disabled={!filters_active}>
          Clear Filters
        </Button>
        <SearchInput
          value={global_filter}
          onChange={(value: string | number) => setGlobalFilter(String(value))}
          className="w-full p-2 font-lg"
          placeholder={DEDUCTION_TABLE_SEARCH_PLACEHOLDER}
        />
        <Button variant="outline" className="text-slate-500" onClick={export_csv} disabled={visible_rows.length === 0}>
          <Download className="-ml-1 h-4" />
          Export CSV
        </Button>
        <ColumnVisibilityMenu table={table} className="text-slate-500" />
      </div>

      <div class="rounded border overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-50">
            {table.getHeaderGroups().map((header_group) => (
              <tr key={header_group.id} class="border-b text-left text-xs text-gray-500">
                {header_group.headers.map((header) => (
                  <th key={header.id} class="px-3 py-2 font-medium whitespace-nowrap">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {visible_rows.length === 0 && (
              <tr>
                <td colSpan={column_count} class="px-3 py-8 text-center text-gray-400">
                  {filters_active ? (
                    <span class="inline-flex items-center gap-2">
                      No rows match these filters.{" "}
                      <button type="button" class="underline" onClick={clear_filters}>
                        Clear them
                      </button>
                    </span>
                  ) : tab === "open" ? (
                    <span class="inline-flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> Nothing here.
                    </span>
                  ) : (
                    <span class="inline-flex items-center gap-2">
                      <Clock className="h-4 w-4" /> Nothing synced in the last 90 days.
                    </span>
                  )}
                </td>
              </tr>
            )}
            {visible_rows.map((row) => {
              const editable = !row.original.cleared_at && !row.original.clearing_pending
              const is_expanded = editable && expanded.has(row.original.entry_id)
              return (
                <Fragment key={row.id}>
                  <tr class={is_expanded ? "bg-gray-50" : "border-b last:border-b-0 hover:bg-gray-50"} data-state={row.getIsSelected() ? "selected" : undefined}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} class="px-3 py-2 align-top">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {is_expanded && (
                    <tr class="border-b bg-gray-50">
                      <td colSpan={column_count} class="px-3 pb-4">
                        <OpenDeductionCodingPanel deduction_id={row.original.deduction_id} className="rounded border bg-white p-3" />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
        <div class="flex items-center justify-between border-t bg-gray-50 px-3 py-2 text-xs text-gray-600">
          <span>
            {visible_rows.length === tab_rows.length ? `${tab_rows.length} row(s)` : `${visible_rows.length} of ${tab_rows.length} row(s)`}
          </span>
          <span class="font-mono">{exactCurrencyFormatter(sum_row_amounts(visible_rows.map((row) => row.original)))}</span>
        </div>
      </div>

      {tab === "open" && selected_rows.length > 0 && (
        <div class="sticky bottom-4 flex items-center justify-between rounded-lg border bg-white px-4 py-3 shadow-lg">
          <div class="text-sm">
            <span class="font-medium">{selected_rows.length} selected</span> ·{" "}
            {exactCurrencyFormatter(sum_row_amounts(selected_rows))}
            {common_origin_month(selected_rows) && (
              <span class="text-gray-500"> · all from {month_label(common_origin_month(selected_rows)!)}</span>
            )}
            {!can_sync && !sage_journal_missing && (
              <span class="text-gray-500"> · deselect rows that aren't ready to clear</span>
            )}
          </div>
          <div class="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setRowSelection({})}>
              Clear selection
            </Button>
            <CodingActions selected_rows={selected_rows} onDone={() => setRowSelection({})} />
            {by_stage && (
              <Button variant="outline" size="sm" onClick={() => setStageDialogOpen(true)}>
                Set stage…
              </Button>
            )}
            <Button size="sm" onClick={() => setClearDialogOpen(true)} disabled={!can_sync}>
              {sync_button_label(data?.accounting_system)}
            </Button>
          </div>
        </div>
      )}

      {clear_dialog_open && (
        <ClearDialog
          entry_ids={selected_rows.map((row) => row.entry_id)}
          selected_rows={selected_rows}
          holding_label={holding_label}
          accounting_system={accounting_system}
          onClose={() => setClearDialogOpen(false)}
          onCleared={handle_changed}
        />
      )}
      {rebill_row && (
        <RebillDialog row={rebill_row} accounting_system={accounting_system} onClose={() => setRebillRow(null)} onRebilled={handle_changed} />
      )}
      {stage_dialog_open && (
        <StageDialog selected_rows={selected_rows} onClose={() => setStageDialogOpen(false)} onChanged={handle_changed} />
      )}
    </div>
  )
}
